import { useState, useCallback, useMemo } from 'react'
import type { VisibilityState, ColumnDef } from '@tanstack/react-table'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = 'felton-table-columns-'

// ---------------------------------------------------------------------------
// Helpers (pure functions)
// ---------------------------------------------------------------------------

function buildColumnKeys<TData>(
  columns: ColumnDef<TData, unknown>[]
): string[] {
  return columns
    .map((col) => {
      if ('accessorKey' in col && typeof col.accessorKey === 'string') {
        return col.accessorKey
      }
      if ('id' in col && typeof col.id === 'string') {
        return col.id
      }
      return null
    })
    .filter((key): key is string => key !== null)
    .sort()
}

function readStoredVisibility(
  tableId: string | undefined,
  columnKeys: string[]
): VisibilityState {
  if (!tableId) {
    return {}
  }

  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${tableId}`)
    if (!raw) {
      return {}
    }

    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {}
    }

    // Check if stored _columnKeys matches current columns
    const storedKeys = parsed._columnKeys
    if (Array.isArray(storedKeys)) {
      const storedSorted = [...storedKeys].sort()
      const currentSorted = [...columnKeys].sort()
      if (
        storedSorted.length !== currentSorted.length ||
        storedSorted.some((key, i) => key !== currentSorted[i])
      ) {
        // Column keys changed -- invalidate stored state
        localStorage.removeItem(`${STORAGE_PREFIX}${tableId}`)
        return {}
      }
    }

    // Extract only valid visibility entries (exclude _columnKeys)
    const visibility: VisibilityState = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (key !== '_columnKeys' && typeof value === 'boolean') {
        visibility[key] = value
      }
    }

    return visibility
  } catch {
    // Invalid JSON or other error -- treat as no stored state
    return {}
  }
}

function writeStoredVisibility(
  tableId: string | undefined,
  visibility: VisibilityState,
  columnKeys: string[]
): void {
  if (!tableId) {
    return
  }

  const payload = {
    ...visibility,
    _columnKeys: columnKeys,
  }

  localStorage.setItem(`${STORAGE_PREFIX}${tableId}`, JSON.stringify(payload))
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseColumnVisibilityOptions<TData> {
  columns: ColumnDef<TData, unknown>[]
  tableId?: string
}

export interface UseColumnVisibilityReturn {
  columnVisibility: VisibilityState
  setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>
  persistVisibility: (visibility: VisibilityState) => void
}

export function useColumnVisibility<TData>({
  columns,
  tableId,
}: UseColumnVisibilityOptions<TData>): UseColumnVisibilityReturn {
  const columnKeys = useMemo(() => buildColumnKeys(columns), [columns])

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => readStoredVisibility(tableId, columnKeys)
  )

  const persistVisibility = useCallback(
    (visibility: VisibilityState) => {
      writeStoredVisibility(tableId, visibility, columnKeys)
    },
    [tableId, columnKeys]
  )

  return {
    columnVisibility,
    setColumnVisibility,
    persistVisibility,
  }
}
